import { createMemo, For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { KIND_LABEL_KEY, type ShownKind } from '../../lib/notification-labels';
import type {
  NotificationCellWrite,
  NotificationSettingsRow,
} from '../../lib/notification-settings';
import { mediumState } from '../../lib/notification-tiers';
import { Panel } from '../Panel';
import { Switch } from '../Switch';

// "Send to another app (webhook)": one switch per topic the server marks
// AVAILABLE for webhook, and nothing for the rest. A switch that could never
// deliver is worse than no switch. Renders nothing when no topic qualifies.
// Kind ids, not row objects, drive the list (see `NotificationTierCard.tsx`).

export function NotificationAdvanced(props: {
  readonly rows: readonly NotificationSettingsRow[];
  readonly onWrite: (cells: readonly NotificationCellWrite[]) => void;
}): JSX.Element {
  const stateOf = (kind: ShownKind) => {
    const row = props.rows.find((r) => r.kind === kind);
    return row === undefined ? ({ kind: 'none' } as const) : mediumState([row], 'webhook');
  };
  const kinds = createMemo(() =>
    props.rows.map((row) => row.kind).filter((kind) => stateOf(kind).kind === 'switch'),
  );
  const checked = (kind: ShownKind): boolean => {
    const state = stateOf(kind);
    return state.kind === 'switch' && state.checked;
  };
  const send = (kind: ShownKind): void =>
    props.onWrite([{ kind, channel: 'webhook', enabled: !checked(kind) }]);

  return (
    <Show when={kinds().length > 0}>
      <Panel title={t('notifications_settings.advanced.heading')} class="notification-advanced">
        <details class="notification-advanced__details">
          <summary class="notification-advanced__summary">
            {t('notifications_settings.advanced.desc')}
          </summary>
          <p class="card__desc">{t('notifications_settings.advanced.hint')}</p>
          <ul class="notification-advanced__topics">
            <For each={kinds()}>
              {(kind) => (
                <li class="notification-advanced__topic">
                  {/* The topic's own words, visible beside the switch (a bare
                      switch tells a sighted person nothing), and the switch's
                      `aria-labelledby` points here, so the visible text and the
                      accessible name are one node and cannot drift. */}
                  <span class="notification-topic__name" id={`notification-webhook-${kind}`}>
                    {t(KIND_LABEL_KEY[kind])}
                  </span>
                  <Switch
                    checked={checked(kind)}
                    aria-labelledby={`notification-webhook-${kind}`}
                    onActivate={() => send(kind)}
                  />
                </li>
              )}
            </For>
          </ul>
        </details>
      </Panel>
    </Show>
  );
}
